import { useState, useMemo } from "react"
import { Link } from "react-router-dom"
import { Search, ChevronRight, Calculator } from "lucide-react"
import { toolsRegistry, TOOL_CATEGORIES, ToolCategory } from "./toolRegistry"

import { APP_NAME } from "@/lib/appConfig"
import { usePageSEO } from "@/lib/usePageSEO"
import PublicNavbar from "@/components/shared/PublicNavbar"
import PublicFooter from "@/components/shared/PublicFooter"

export default function ToolsHubPage() {
  const [searchQuery, setSearchQuery] = useState("")

  usePageSEO({
    title: `Free Landlord Tools | ${APP_NAME}`,
    description: `21 free property calculators for landlords — rental yield, cash flow, stamp duty, rent increase, depreciation and more. No sign-up required.`,
    path: "/tools",
    jsonLd: [
      {
        "@type": "CollectionPage",
        name: `Free Landlord Tools | ${APP_NAME}`,
        description: "Free property calculators for landlords. No sign-up required.",
        url: "https://rent-keep.com/tools",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: toolsRegistry.length,
          itemListElement: toolsRegistry.map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: t.title,
            url: `https://rent-keep.com/tools/${t.slug}`,
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://rent-keep.com" },
          { "@type": "ListItem", position: 2, name: "Free Tools", item: "https://rent-keep.com/tools" },
        ],
      },
    ],
  })

  const filteredTools = useMemo(() => {
    if (!searchQuery.trim()) return toolsRegistry
    const lowerQuery = searchQuery.toLowerCase()
    return toolsRegistry.filter(
      (t) =>
        t.title.toLowerCase().includes(lowerQuery) ||
        t.description.toLowerCase().includes(lowerQuery) ||
        t.keywords.some((k) => k.toLowerCase().includes(lowerQuery))
    )
  }, [searchQuery])

  const popularTools = toolsRegistry.slice(0, 5)

  // Group filtered tools by category
  const toolsByCategory = useMemo(() => {
    const grouped = {} as Record<ToolCategory, typeof toolsRegistry>
    ;(Object.keys(TOOL_CATEGORIES) as ToolCategory[]).forEach((cat) => {
      grouped[cat] = filteredTools.filter((t) => t.category === cat)
    })
    return grouped
  }, [filteredTools])

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col pt-16">
      <PublicNavbar />
      {/* Search Header Hero */}
      <section className="bg-white border-b border-slate-200 pt-12 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
            Free Landlord Tools
          </h1>
          <p className="mt-4 text-xl text-slate-600 max-w-2xl mx-auto">
            Instant calculators and analysis tools to help you manage properties smarter.
            No sign-up required.
          </p>

          <div className="mt-8 max-w-xl mx-auto relative cursor-text">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-4 border border-slate-300 rounded-xl leading-5 bg-white placeholder-slate-500 focus:outline-none focus:placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-primary sm:text-lg shadow-sm transition-all"
              placeholder="Search tools, calculators, rent rules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16 w-full">
        {/* Popular section (only show if no search query) */}
        {!searchQuery && (
          <section>
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Most Popular</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {popularTools.map((tool) => (
                <ToolCard key={tool.slug} tool={tool} compact />
              ))}
            </div>
          </section>
        )}

        {/* Categories */}
        {(Object.entries(TOOL_CATEGORIES) as [ToolCategory, typeof TOOL_CATEGORIES[ToolCategory]][]).map(([catKey, catDef]) => {
          const toolsInCat = toolsByCategory[catKey]
          if (toolsInCat.length === 0) return null

          return (
            <section key={catKey}>
              <div className="flex items-center gap-3 mb-6 border-b border-slate-200 pb-4">
                <div className={`p-2 rounded-lg ${catDef.color}`}>
                  <catDef.icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{catDef.label}</h2>
                  <p className="text-slate-500 text-sm">{catDef.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {toolsInCat.map((tool) => (
                  <ToolCard key={tool.slug} tool={tool} />
                ))}
              </div>
            </section>
          )
        })}

        {filteredTools.length === 0 && (
          <div className="text-center py-20">
            <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-slate-900">No tools found</h3>
            <p className="text-slate-500 mt-2">Try adjusting your search terms.</p>
            <button 
              onClick={() => setSearchQuery("")}
              className="mt-6 text-primary font-medium hover:underline"
            >
              Clear search
            </button>
          </div>
        )}
      </main>
      
      <PublicFooter />
    </div>
  )
}

function ToolCard({ tool, compact = false }: { tool: typeof toolsRegistry[number], compact?: boolean }) {
  const Icon = tool.icon

  return (
    <Link
      to={`/tools/${tool.slug}`}
      className={`group bg-white border border-slate-200 rounded-xl p-5 block transition-all hover:shadow-lg hover:border-primary/50 relative ${compact ? 'flex flex-col h-full' : ''}`}
    >
      <div className="flex items-start gap-4">
        <div className={`p-2.5 rounded-lg bg-slate-50 text-slate-600 group-hover:bg-primary/10 group-hover:text-primary transition-colors ${compact ? 'mb-3' : ''}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className={`${compact ? 'flex-1' : ''}`}>
          <h3 className={`font-semibold text-slate-900 group-hover:text-primary transition-colors ${compact ? 'text-base leading-tight' : 'text-lg'}`}>
            {compact ? tool.shortTitle : tool.title}
          </h3>
          {!compact && (
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed line-clamp-2">
              {tool.description}
            </p>
          )}
        </div>
      </div>
      
      <div className={`mt-4 flex items-center justify-end text-sm ${compact ? 'mt-auto pt-4' : 'pt-2'} border-t border-slate-50/0 group-hover:border-slate-100 transition-colors`}>
        <span className="text-primary font-medium flex items-center opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all">
          Open <ChevronRight className="w-4 h-4 ml-0.5" />
        </span>
      </div>
    </Link>
  )
}
